import { FC, useState } from 'react'
import { createPortal } from 'react-dom'
import { ReactComponent as CloseIcon } from 'src/assets/svg/modal-close.svg'
import { useDisableScroll } from 'src/hooks/useDisableScroll'
import { BrandIssue } from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'
import { TruncatedText } from 'src/pages/ReviewAnalysis/TruncatedText'
import styles from './topBrandIssues.module.scss'
import { getPlatformIcon } from '../../platformIcons'

const WARNING_ICON_FILTER =
  'invert(52%) sepia(99%) saturate(1200%) hue-rotate(340deg) brightness(105%)'

interface IssueDetailsModalProps {
  issue: BrandIssue | null
  onClose: () => void
}

// Full-issue view: every quote is shown untruncated, so long reviews stay
// readable (the old hover tooltip clipped them). Portaled to <body> — the app
// zooms .mainContainer, which breaks position: fixed overlays rendered inside.
const IssueDetailsModal: FC<IssueDetailsModalProps> = ({ issue, onClose }) => {
  useDisableScroll(Boolean(issue))

  if (!issue) return null

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  return createPortal(
    <div
      className={styles.modalOverlay}
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div className={styles.modalHeading}>
            <img
              alt="warning"
              className={styles.warningIcon}
              src="/warning.svg"
              style={{ filter: WARNING_ICON_FILTER }}
            />
            <div>
              <h2 className={styles.modalTitle}>{issue.name}</h2>
              <p className={styles.modalMeta}>
                {issue.location && <>{issue.location} · </>}
                {issue.percentage}% · {issue.reviewCount} reviews · Avg ★{' '}
                {issue.avgRating.toFixed(1)}
              </p>
            </div>
          </div>
          <button
            className={styles.modalCloseBtn}
            onClick={onClose}
            type="button"
          >
            <CloseIcon />
          </button>
        </div>

        <div className={styles.quoteList}>
          {issue.tooltip.quotes.map((q) => (
            <p className={styles.quote} key={q}>
              {q}
            </p>
          ))}
        </div>

        <div className={styles.platformRow}>
          <span className={styles.platformLabel}>Platform:</span>
          {issue.tooltip.platforms.map(({ name: pName, platform }) => {
            const Icon = getPlatformIcon(platform)
            return (
              <span className={styles.platform} key={pName}>
                <Icon className={styles.platformIcon} />
                {pName}
              </span>
            )
          })}
        </div>
      </div>
    </div>,
    document.body
  )
}

interface IssueCardProps extends BrandIssue {
  onOpen: () => void
}

const IssueCard: FC<IssueCardProps> = ({
  avgRating,
  location,
  name,
  onOpen,
  percentage,
  reviewCount,
}) => (
  <div className={styles.issueCard} onClick={onOpen}>
    <div className={styles.issueHeading}>
      <img
        alt="warning"
        className={styles.warningIcon}
        src="/warning.svg"
        style={{ filter: WARNING_ICON_FILTER }}
      />
      <TruncatedText className={styles.issueName} text={name} />
    </div>
    <div className={styles.barWrapper}>
      <div className={styles.barFill} style={{ width: `${percentage}%` }} />
    </div>
    {location && <span className={styles.locationChip}>{location}</span>}
    <div className={styles.issueMetaRow}>
      <p className={styles.issueMeta}>
        {reviewCount} reviews · Avg <span className={styles.metaStar}>★</span>{' '}
        {avgRating.toFixed(1)}
      </p>
      <span className={styles.percentBadge}>{percentage}%</span>
    </div>
  </div>
)

interface TopBrandIssuesProps {
  issues: BrandIssue[]
}

// The viewport/list split keeps this card OUT of the row-height equation: the
// absolutely-positioned list contributes no intrinsic height, so the row is
// always as tall as the left widget (cards or chart) and the issues scroll
// within whatever height that gives us.
export const TopBrandIssues: FC<TopBrandIssuesProps> = ({ issues }) => {
  const [openIssue, setOpenIssue] = useState<BrandIssue | null>(null)

  return (
    <div className={styles.listViewport}>
      <div className={styles.list}>
        {issues.map((issue) => (
          <IssueCard
            // an issue repeats across locations
            key={`${issue.name}::${issue.location ?? ''}`}
            {...issue}
            onOpen={() => setOpenIssue(issue)}
          />
        ))}
      </div>
      <IssueDetailsModal issue={openIssue} onClose={() => setOpenIssue(null)} />
    </div>
  )
}
